import React, { useMemo } from 'react';
import type { MenuProps } from 'antd';
import type { RowPrimaryAction } from '@/components/content-planner/shared';
import { ContentListRow, LIST_GRID_TEMPLATE } from './ContentListRow';
import { ContentListGroupHeader } from './ContentListGroupHeader';
import { deriveGroup, type GroupKey } from './deriveGroup';
import { byGenerationRecency } from '../sortItems';
import type { CalendarItem } from '@/types/contentPlanner';

export interface ContentListViewProps {
  items: CalendarItem[];
  isLoading?: boolean;
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onRowClick?: (item: CalendarItem) => void;
  getRowMenuItems?: (item: CalendarItem) => MenuProps['items'];
  getRowPrimaryActions?: (item: CalendarItem) => RowPrimaryAction[];
}

// v1: Pinned hidden — no `is_pinned` field on CalendarItem (per integration plan §3.4).
const VISIBLE_GROUPS: GroupKey[] = ['progress', 'queue'];

const EMPTY_MESSAGE: Record<GroupKey, string> = {
  pinned: 'No pinned articles.',
  progress: 'Nothing in progress yet.',
  queue: 'Backlog is empty — add an idea to get started.',
};

export const ContentListView: React.FC<ContentListViewProps> = ({
  items,
  isLoading = false,
  selectedIds,
  onToggleSelect,
  onRowClick,
  getRowMenuItems,
  getRowPrimaryActions,
}) => {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const grouped = useMemo(() => {
    const buckets: Record<GroupKey, CalendarItem[]> = {
      pinned: [],
      progress: [],
      queue: [],
    };
    for (const item of items) {
      buckets[deriveGroup(item)].push(item);
    }
    // Float newly-started generations (and most-recent items) to the top of each group.
    buckets.pinned.sort(byGenerationRecency);
    buckets.progress.sort(byGenerationRecency);
    buckets.queue.sort(byGenerationRecency);
    return buckets;
  }, [items]);

  return (
    <div
      style={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        background: 'var(--color-warm-bg)',
      }}
    >
      {/* Column headers */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: LIST_GRID_TEMPLATE,
          minWidth: 900,
          alignItems: 'center',
          gap: 14,
          padding: '8px 28px',
          background: 'var(--color-warm-bg)',
          borderBottom: '1px solid var(--color-warm-border)',
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--color-ink-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <span />
        <span style={{ textAlign: 'center' }}>Score</span>
        <span>Title</span>
        <span>Status</span>
        <span style={{ textAlign: 'center' }}>Issues</span>
        <span>Keyword</span>
        <span>Author</span>
        <span style={{ textAlign: 'right' }}>Performance</span>
        <span />
      </div>

      {isLoading ? (
        <div
          style={{
            padding: '24px 28px',
            fontSize: 12,
            color: 'var(--color-ink-3)',
            background: 'var(--color-paper)',
            borderBottom: '1px solid var(--color-warm-border)',
          }}
        >
          Loading articles...
        </div>
      ) : VISIBLE_GROUPS.map((group) => {
        const rows = grouped[group];
        return (
          <div key={group}>
            <ContentListGroupHeader group={group} count={rows.length} />
            {rows.length === 0 ? (
              <div
                style={{
                  padding: '20px 28px',
                  fontSize: 12,
                  color: 'var(--color-ink-3)',
                  background: 'var(--color-paper)',
                  borderBottom: '1px solid var(--color-warm-border)',
                }}
              >
                {EMPTY_MESSAGE[group]}
              </div>
            ) : (
              rows.map((item) => (
                <ContentListRow
                  key={item.id}
                  item={item}
                  selected={selectedSet.has(item.id)}
                  onToggleSelect={onToggleSelect}
                  onClick={onRowClick}
                  getRowMenuItems={getRowMenuItems}
                  getRowPrimaryActions={getRowPrimaryActions}
                />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ContentListView;
